// Conditions d'un raid : heure et météo, choisies avant le départ. Elles changent l'ambiance (client) et le jeu (serveur) :
// la vue et l'ouïe des pillards et des infectés, le nombre d'infectés, le butin.

export type TimeOfDay = 'day' | 'dusk' | 'night';
export type Weather = 'clear' | 'overcast' | 'rain' | 'fog';
export interface RaidCond { time: TimeOfDay; weather: Weather }

export const TIMES: { id: TimeOfDay; name: string; note: string }[] = [
  { id: 'day', name: 'Jour', note: 'Bonne visibilité.' },
  { id: 'dusk', name: 'Crépuscule', note: 'Lumière basse, ombres longues.' },
  { id: 'night', name: 'Nuit', note: 'Lampe torche. On te voit moins, tu vois moins. Plus d’infectés, meilleur butin.' },
];
export const WEATHERS: { id: Weather; name: string; note: string }[] = [
  { id: 'clear', name: 'Clair', note: 'Aucun effet.' },
  { id: 'overcast', name: 'Couvert', note: 'Lumière grise et douce.' },
  { id: 'rain', name: 'Pluie', note: 'Le bruit porte moins loin. Vue réduite.' },
  { id: 'fog', name: 'Brouillard', note: 'On ne voit pas loin. Butin un peu meilleur.' },
];
export const DEFAULT_COND: RaidCond = { time: 'dusk', weather: 'clear' };

export const validCond = (c: unknown): RaidCond => {
  const o = (c ?? {}) as Partial<RaidCond>;
  return { time: TIMES.some(t => t.id === o.time) ? o.time! : DEFAULT_COND.time, weather: WEATHERS.some(w => w.id === o.weather) ? o.weather! : DEFAULT_COND.weather };
};
export const condKey = (c: RaidCond) => `${c.time}-${c.weather}`;
export const condName = (c: RaidCond) => `${TIMES.find(t => t.id === c.time)!.name} · ${WEATHERS.find(w => w.id === c.weather)!.name}`;

// Effets sur le jeu : sight multiplie la distance de vue des IA, hearing celle à laquelle elles entendent ;
// horde multiplie le nombre d'infectés ; loot : chance d'un objet de plus dans chaque meuble
export function condEffects(c: RaidCond) {
  const t = { day: [1, 1, 1, 0], dusk: [0.85, 1, 1.1, 0.1], night: [0.55, 1.1, 1.3, 0.4] }[c.time];
  const w = { clear: [1, 1, 0], overcast: [0.95, 1, 0], rain: [0.8, 0.7, 0.05], fog: [0.6, 1, 0.2] }[c.weather];
  return { sight: t[0]! * w[0]!, hearing: t[1]! * w[1]!, horde: t[2]!, loot: t[3]! + w[2]! };
}

// ---------------------------------------------------------------- Heure et météo du serveur
// Un seul cycle pour tout le monde (comme une horloge de jeu) : les joueurs qui cherchent un raid au même moment
// tombent dans le même, au lieu d'être répartis entre douze combinaisons.
// Journée d'une heure : jour 28 min, crépuscule 8 min, nuit 18 min, aube (crépuscule) 6 min.
// Météo tirée toutes les 15 minutes (même tirage pour tous, d'après l'heure) : clair 50 %, couvert 22 %, pluie 16 %, brouillard 12 %.
const DAY_MS = 60 * 60_000, WEATHER_MS = 15 * 60_000;
const PHASES: [TimeOfDay, number][] = [['day', 28], ['dusk', 8], ['night', 18], ['dusk', 6]];
function timeAt(ms: number): { time: TimeOfDay; ends: number } {
  const start = Math.floor(ms / DAY_MS) * DAY_MS;
  let t = start;
  for (const [time, min] of PHASES) { t += min * 60_000; if (ms < t) return { time, ends: t }; }
  return { time: 'day', ends: start + DAY_MS };
}
function weatherAt(ms: number): { weather: Weather; ends: number } {
  const slot = Math.floor(ms / WEATHER_MS);
  const h = Math.sin(slot * 12.9898 + 78.233) * 43758.5453, r = h - Math.floor(h);
  const weather: Weather = r < 0.5 ? 'clear' : r < 0.72 ? 'overcast' : r < 0.88 ? 'rain' : 'fog';
  return { weather, ends: (slot + 1) * WEATHER_MS };
}
export const serverCond = (ms: number): RaidCond => ({ time: timeAt(ms).time, weather: weatherAt(ms).weather });
// Conditions actuelles, et quand et vers quoi elles changent ensuite
export function condSchedule(ms: number): { cond: RaidCond; changeAt: number; next: RaidCond } {
  const changeAt = Math.min(timeAt(ms).ends, weatherAt(ms).ends);
  return { cond: serverCond(ms), changeAt, next: serverCond(changeAt + 1) };
}
